import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col } from 'react-bootstrap';
import Message from '../../../components/Message';
import Loader from '../../../components/Loader';
import FormContainer from '../../../components/FormContainer';
import { toast } from 'react-toastify';
import { useLayout } from '../../../context/LayoutContext';

import { Card } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import {
  CATEGORY_OF_STAFF,
  REASON_FOR_REQUEST,
} from '../../../utils/enums';

import {
  useGetShiftByIdQuery,
  useUpdateShiftMutation,
} from '../../../slices/shiftApiSlice';
import { useGetHospitalLocationsQuery } from '../../../slices/hospitalLocationApiSlice';

const ShiftWardEditScreen = () => {
  const { id: shiftId } = useParams();
  const navigate = useNavigate();
  const { setFluid } = useLayout();
  const { userInfo } = useSelector((state) => state.auth);

  const [startDateTime, setStartDateTime] = useState('');
  const [endDateTime, setEndDateTime] = useState('');

  const [actualStart, setActualStart] = useState('');
  const [actualFinish, setActualFinish] = useState('');
  const [actualPersonWorked, setActualPersonWorked] = useState('');
 
  const [actualStatus, setActualStatus] = useState('');

  const [hospitalLocationId, setHospitalLocationId] = useState('');
  const [wardDepartmentArea, setWardDepartmentArea] = useState('');
  const [reasonForRequest, setReasonForRequest] = useState('');
  const [reasonForRequestDetails, setReasonForRequestDetails] = useState('');
  const [categoryOfStaff, setCategoryOfStaff] = useState('');
  const [escort, setEscort] = useState(false);
  const [special, setSpecial] = useState(false);
  const [eccNumber, setEccNumber] = useState('');
  const [specialRequirements, setSpecialRequirements] = useState('');
  const [comments, setComments] = useState('');
  const [stage2WardApprovalManager, setStage2WardApprovalManager] = useState('');
  const [copyActualsChecked, setCopyActualsChecked] = useState(false);
  
  // Track if location has been changed from original
  const [locationChanged, setLocationChanged] = useState(false);
  const [originalLocationId, setOriginalLocationId] = useState('');

  const {
    data: shift,
    isLoading: loadingShift,
    error: errorShift,
  } = useGetShiftByIdQuery(shiftId);

  const {
    data: hospitalLocations,
    isLoading: loadingLocations,
    error: errorLocations,
  } = useGetHospitalLocationsQuery();

  const [updateShift, { isLoading: loadingUpdate }] = useUpdateShiftMutation();

  useEffect(() => {
    if (shift && hospitalLocations?.length > 0) {
      setStartDateTime(shift.startDateTime?.slice(0, 16));
      setEndDateTime(shift.endDateTime?.slice(0, 16));

      const selectedHospitalId =
        typeof shift.hospitalLocationId === 'string'
          ? shift.hospitalLocationId
          : shift.hospitalLocationId?._id;

      setHospitalLocationId(selectedHospitalId);
      setOriginalLocationId(selectedHospitalId); // Store original for comparison

      const selectedLocation = hospitalLocations.find(
        (loc) => loc._id === selectedHospitalId
      );
      setWardDepartmentArea(selectedLocation?.wardDepartmentArea || shift.wardDepartmentArea);

      setReasonForRequest(shift.reasonForRequest);
      setReasonForRequestDetails(shift.reasonForRequestDetails);
      setCategoryOfStaff(shift.categoryOfStaff);
      setEscort(shift.escort);
      setSpecial(shift.special);
      setEccNumber(shift.eccNumber);
      setSpecialRequirements(shift.specialRequirements);
      setComments(shift.comments);
      setActualStart(shift.actualStart);
      setActualFinish(shift.actualFinish);
      setActualPersonWorked(shift.actualPersonWorked);
      setActualStatus(shift.actualStatus);
      setStage2WardApprovalManager(userInfo._id);
    }
  }, [shift, hospitalLocations, userInfo]);

  // Handle hospital location change
  const handleHospitalLocationChange = (e) => {
    const newHospitalId = e.target.value;
    setHospitalLocationId(newHospitalId);
    
    // Find the selected location and update wardDepartmentArea
    const selectedLocation = hospitalLocations.find(loc => loc._id === newHospitalId);
    if (selectedLocation) {
      setWardDepartmentArea(selectedLocation.wardDepartmentArea);
      
      // Check if location changed from original
      setLocationChanged(newHospitalId !== originalLocationId);
    }
  };

  // SEPARATE FUNCTION: Save only location changes and close
  const handleSaveLocationOnly = async () => {
    if (!locationChanged) {
      toast.info('No location changes to save');
      return;
    }

    try {
      // Only send location-related fields
      await updateShift({
        shiftId,
        hospitalLocationId,
        wardDepartmentArea,
        locationUpdated: true, // Flag for backend
        updatedBy: userInfo._id,
      }).unwrap();
      
      toast.success('Shift location updated successfully');
      navigate('/manager/approved-filled'); // Close form and go back
      
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  // SEPARATE FUNCTION: Save all changes (actuals + location if changed) and close
  const handleSaveAllChanges = async () => {
    // Validate actuals fields
    if (!actualStart.trim() || !actualFinish.trim() || !actualPersonWorked.trim()) {
      alert('Please fill in all actuals details before proceeding.');
      return;
    }

    const validStatuses = ['Complete', 'Not-Worked'];
    if (!actualStatus || !validStatuses.includes(actualStatus)) {
      alert('Please select a valid Actual Status: either "Complete" or "Not-Worked".');
      return;
    }

    try {
      // Prepare all update data
      const updateData = {
        shiftId,
        // Location fields (will only update if changed)
        hospitalLocationId,
        wardDepartmentArea,
        // Actuals fields
        actualStart,
        actualFinish,
        actualPersonWorked,
        actualStatus,
        comments,
        stage2WardApprovalManager,
        updatedBy: userInfo._id,
      };

      // Add flags if location changed
      if (locationChanged) {
        updateData.locationUpdated = true;
      }

      await updateShift(updateData).unwrap();
      
      toast.success('Shift updated successfully');
      navigate('/manager/approved-filled'); // Close form and go back
      
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  // Handle cancel button - go back without saving
  const handleCancel = () => {
    navigate('/manager/approved-filled');
  };

  useEffect(() => {
    setFluid(true);
    return () => {
      setFluid(false);
    };
  }, [setFluid]);

  return (
    <FormContainer>
      <h2 className="mb-3">Edit Shift Details</h2>
      
      {loadingUpdate && <Loader />}
      {loadingShift || loadingLocations ? (
        <Loader />
      ) : errorShift ? (
        <Message variant="danger">
          {errorShift?.data?.message || errorShift.error}
        </Message>
      ) : errorLocations ? (
        <Message variant="danger">
          {errorLocations?.data?.message || errorLocations.error}
        </Message>
      ) : (
        <>
          {/* Planned Shift Details Card - With separate save button */}
          <Card className="mb-4 custom-card-bg">
            <Card.Header className="fw-bold d-flex justify-content-between align-items-center">
              <span>Planned Shift Details</span>
              {locationChanged && (
                <Button 
                  variant="success" 
                  size="sm"
                  onClick={handleSaveLocationOnly}
                  disabled={loadingUpdate}
                >
                  {loadingUpdate ? 'Saving...' : 'Save Location Change Only'}
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col md={2}>
                  <Form.Group controlId="hospitalLocationId">
                    <Form.Label>Hospital Location</Form.Label>
                    <Form.Select 
                      value={hospitalLocationId} 
                      onChange={handleHospitalLocationChange}
                      className={locationChanged ? 'border-warning' : ''}
                    >
                      <option value="">Select...</option>
                      {hospitalLocations.map((location) => (
                        <option key={location._id} value={location._id}>
                          {location.wardDepartmentArea} 
                        </option>
                      ))}
                    </Form.Select>
                    {locationChanged && (
                      <Form.Text className="text-warning">
                        ⚠️ Location changed - click "Save Location Change Only" to update
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Group controlId="categoryOfStaff">
                    <Form.Label>Category of Staff</Form.Label>
                    <Form.Control
                      type="text"
                      value={categoryOfStaff}
                      readOnly
                      plaintext
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId="reasonForRequest">
                    <Form.Label>Reason for Request</Form.Label>
                    <Form.Control
                      type="text"
                      value={reasonForRequest}
                      readOnly
                      plaintext
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId="reasonForRequestDetails">
                    <Form.Label>Reason Details</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={reasonForRequestDetails}
                      readOnly
                      plaintext
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group controlId="startDateTime">
                    <Form.Label>Planned Start</Form.Label>
                    <Form.Control
                      type="text"
                      value={new Date(startDateTime).toLocaleString()}
                      readOnly
                      plaintext
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="endDateTime">
                    <Form.Label>Planned End</Form.Label>
                    <Form.Control
                      type="text"
                      value={new Date(endDateTime).toLocaleString()}
                      readOnly
                      plaintext
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="eccNumber">
                    <Form.Label>ECC Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={eccNumber}
                      readOnly
                      plaintext
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label className="fw-bold">Selections</Form.Label>
                  <div>
                    <span className="me-3">Escort: {escort ? 'Yes' : 'No'}</span>
                    <span>Special: {special ? 'Yes' : 'No'}</span>
                  </div>
                </Col>
              </Row>

              <Form.Group controlId="specialRequirements">
                <Form.Label>Special Requirements</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={specialRequirements}
                  readOnly
                  plaintext
                />
              </Form.Group>
            </Card.Body>
          </Card>

          {/* Actuals Card - With main save button */}
          <Card className="mb-4 custom-card-bg">
            <Card.Header className="fw-bold">Ward Approvals: Actuals</Card.Header>
            <Card.Body>
              <Form.Group controlId="copyActualsCheckbox" className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Copy planned Start/End and Staff into Actuals"
                  checked={copyActualsChecked}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setCopyActualsChecked(checked);

                    if (checked) {
                      setActualStart(startDateTime);
                      setActualFinish(endDateTime);
                      setActualPersonWorked(shift.filledByName || '');
                    }
                  }}
                />
              </Form.Group>

              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group controlId="actualStart">
                    <Form.Label>Actual Start</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={actualStart}
                      onChange={(e) => setActualStart(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="actualFinish">
                    <Form.Label>Actual Finish</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={actualFinish}
                      onChange={(e) => setActualFinish(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="actualPersonWorked">
                    <Form.Label>Actual Person Worked</Form.Label>
                    <Form.Control
                      type="text"
                      value={actualPersonWorked}
                      onChange={(e) => setActualPersonWorked(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="actualStatus">
                    <Form.Label>Actual Status</Form.Label>
                    <Form.Select
                      value={actualStatus}
                      onChange={(e) => setActualStatus(e.target.value)}
                    >
                      <option value="">Select...</option>
                      <option value="Complete">Complete</option>
                      <option value="Not-Worked">Not-Worked</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group controlId="comments">
                <Form.Label>Comments</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                />
              </Form.Group>
            </Card.Body>
          </Card>

          {/* Action Buttons */}
          <div className="d-flex gap-2 justify-content-between">
            <div>
              <Button 
                type="button" 
                variant="primary" 
                onClick={handleSaveAllChanges}
                disabled={loadingUpdate}
              >
                {loadingUpdate ? 'Saving...' : 'Save All Changes & Close'}
              </Button>
            </div>
            <div>
              <Button 
                type="button" 
                variant="secondary" 
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          </div>
        </>
      )}
    </FormContainer>
  );
};

export default ShiftWardEditScreen;


